import React, { useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, Row } from 'react-bootstrap'
import dynamic from 'next/dynamic'

import styles from './../style.module.scss'
import { allRoutes } from '@shared-submodule/sm-constants/allRoutes'
import { useLazyQuery } from '@apollo/client'
import { isBottomReached, mapArticleData } from '@shared-submodule/utils'
import { HOME_PAGE_ARTICLE } from '@graphql/home/home.query'
import { articleLoader } from '@shared/libs/allLoader'
import useWindowSize from '@shared/hooks/windowSize'
import GlobalEventsContext from '@shared/components/global-events/GlobalEventsContext'
// import StaticBanner from '@shared/components/staticBanner'
import { ENUM_SERIES_CATEGORY_PAGES } from '@shared-submodule/sm-enum'
// import WordleWidget from '@shared/components/wordleWidget'
import Formula1Widget from '@shared/components/widgets/f1Widget'
import { reviveAdData } from '@shared/components/reviveAds/reviveAdData'
import CardBottomButton from '@shared/components/cardBottomButton'
import HeaderCardsHeader from '@shared/components/header/headerCardsHome'
import LiveMatchCard from '@shared/components/liveMatchCard'
import { WIDGET } from '@shared-submodule/sm-constants'

const ReviveAds = dynamic(() => import('@shared/components/reviveAds'), { ssr: false })

const ArticleGrid = dynamic(() => import('@shared/components/article/articleGrid'), { loading: () => articleLoader(['g']) })
const ArticleMedium = dynamic(() => import('@shared/components/article/articleMedium'))
const AllWidget = dynamic(() => import('@shared/components/allWidget'), { ssr: false })

const CardBody = ({ cat, ind }) => {
  const firstCard = cat?.aArticle?.[0]
  const nextTwoCards = cat?.aArticle?.slice(1, 3)

  return (
    <div className={`${styles.cardContainer}`}>
      {cat?.bScoreCard && cat?.aScore?.length !== 0 && <LiveMatchCard data={cat} />}
      <div className={`${styles.cardBody} ${!cat?.bScoreCard || cat?.aScore?.length === 0 ? 'mt-3 mt-md-0' : ''}`}>
        <Col xs={12} className={`${ind === 0 ? styles.firstCard : ''}`}>
          {firstCard && (
            <Row>
              <Col xs={12} sm={12} lg={12}>
                <ArticleMedium data={firstCard} isFirstImg={ind === 0} />
              </Col>
            </Row>
          )}
          <Row>
            {nextTwoCards.map((item, index) => (
              <React.Fragment key={`${item?._id}${index}`}>
                <Col key={`${item?._id}${index}`} xs={12} sm={12} lg={6} className="d-none d-md-flex">
                  <ArticleGrid data={item} isFirstImg={ind === 0} />
                </Col>
                <Col xs={12} sm={12} lg={12} className="d-flex d-md-none">
                  <ArticleMedium data={item} />
                </Col>
              </React.Fragment>
            ))}
          </Row>
        </Col>
      </div>
    </div>
  )
}
CardBody.propTypes = {
  cat: PropTypes.object,
  ind: PropTypes.number
}

function HomePageArticle({ articles }) {
  const [article, setArticle] = useState(mapArticleData(articles) || [])
  const [loading, setLoading] = useState(false)
  const payload = useRef({ nLimit: 3, nSkip: 1 })
  const total = useRef(4)
  const isLoading = useRef(false)
  const [width] = useWindowSize()
  const { stateGlobalEvents } = useContext(GlobalEventsContext)
  // const { isLoaded } = useOnMouseAndScroll()
  // let banInd = -1

  // const { data, fetchMore } = useQuery(HOME_PAGE_ARTICLE, { variables: { input: payload.current } })

  // const latestArticle = data?.getHomePageArticle?.aResults?.length

  useEffect(() => {
    if (article) {
      isLoading.current = false
      setLoading(false)
      isBottomReached(article[article?.length - 1]?.sSlug, isReached)
    }
  }, [article])

  const [fetchMore, { data }] = useLazyQuery(HOME_PAGE_ARTICLE, { variables: { input: payload.current } })

  useEffect(() => {
    if (data?.getHomePageArticle) {
      handleArticleResponse(data?.getHomePageArticle)
    }
  }, [data])

  useEffect(() => {
    if (stateGlobalEvents?.homeArticle?.length) {
      const cloneArticls = article
      stateGlobalEvents?.homeArticle.forEach(match => {
        let currentMatch = cloneArticls.find(item => item?.oScore?.iMatchId === match?.iMatchId)
        if (currentMatch) {
          currentMatch = { ...currentMatch, oScore: { ...currentMatch?.oScore, ...match } }
          const index = cloneArticls.findIndex(s => s?.iSeriesId === currentMatch?.iSeriesId)
          cloneArticls[index] = currentMatch
        }
      })
      setArticle(cloneArticls)
    }
  }, [stateGlobalEvents])

  async function isReached(reach) {
    if (reach && !isLoading.current && total.current !== 0) {
      isLoading.current = true
      setLoading(true)
      const newSkip = (payload.current.nSkip += 1)
      handleMoreData(newSkip)
    }
  }

  function handleMoreData(number) {
    const newPayload = { nLimit: 3, nSkip: number }
    fetchMore({ variables: { input: newPayload } })
  }

  function handleArticleResponse(data) {
    total.current = data?.aResults?.length
    setArticle([...article, ...mapArticleData(data?.aResults)])
  }

  function getCustomURL(aCustomSeo = []) {
    const URLs = {}
    aCustomSeo?.forEach(e => {
      URLs[ENUM_SERIES_CATEGORY_PAGES[e.eSubType]] = `/${e?.sSlug}`
    })
    return URLs
  }

  const { addLeft, add300 } = reviveAdData
  const randomAd = Math.random() < 0.5 ? addLeft : add300
  const dataFormatter = data => {
    return data.map(item => {
      const updatedArticles =
        item?.aArticle?.flatMap(article => {
          if (article.sType === 'nBig' || article.sType === 'nMed') {
            return [article]
          } else if (article.sType === 'nMedGrid' || article.sType === 'nGrid') {
            return article.mappedArticle || []
          }
          return []
        }) || []
      return {
        ...item,
        aArticle: updatedArticles
      }
    })
  }
  const articleData = dataFormatter(article)
  return (
    <>
      {articleData?.map((cat, ind) => {
        const rewriteURLs = cat?.eType === 'as' ? getCustomURL(cat?.aScore?.[0]?.oSeriesSeos?.oSeo) : {}

        const routeLinks = {}

        if (rewriteURLs?.fixtures) routeLinks.fixtures = rewriteURLs.fixtures
        if (rewriteURLs?.stats) routeLinks.stats = rewriteURLs.stats
        if (rewriteURLs?.squads) routeLinks.squads = rewriteURLs.squads

        const headerData = {
          title: cat.sName,
          slug: cat?.sSlug ? (cat?.eType === 's' ? `/${cat.sSlug}` : allRoutes.seriesHome(cat.sSlug)) : null,
          routeLinks
        }
        // if (ind % 2 === 0) {
        //   ++banInd
        //   if ((width < 768 && banInd > 5) || (width > 767 && banInd > 2)) {
        //     banInd = 0
        //   }
        // }
        return (
          <React.Fragment key={`${cat.sSlug}${ind}`}>
            {/* {ind % 2 === 0 && ind !== 0 && <StaticBanner isTop />} */}
            {/* Add static banner after every two category articles */}
            {/* {width < 768 && ind % 3 === 0 && <StaticBannerSecond adType="secondAdRect_Left" link="home_left_banner" />} */}
            {/* Add static banner after every two category articles */}
            {/* {width < 768 && ind % 5 === 0 && ind !== 0 && <StaticBannerSecond adType="secondAd_Rect" link="home_right_banner" />} */}
            {/* {ind === 1 && width < 767 && <WPLWidget />} */}
            {/* Add static banner after every two category articles */}
            {ind % 2 === 0 && ind !== 0 && width < 1000 && <ReviveAds {...randomAd} className={'d-flex justify-content-center'} />}
            <section id={cat.sSlug} className={`${styles.homeArticles}`}>
              <div className={`common-box ${styles.boxBtmPad} p-0`}>
                <HeaderCardsHeader data={headerData} i18nKeyText />
                <CardBody cat={cat} ind={ind} />
                {cat?.sSlug && (
                  <div className="text-center">
                    <CardBottomButton
                      text="Read More"
                      link={cat?.eType === 's' ? `/${cat?.sSlug}` : allRoutes.seriesHome(cat?.sSlug)}
                      internalLink
                    />
                  </div>
                )}
              </div>
            </section>
            {/* {ind === 1 && width < 767 && <CTMwebWidget />} */}
            {/* {
              ind % 2 === 0 && isLoaded && <ATSAds />
            } */}
            {/* {
              ind === 0 && width < 991 && isLoaded && <DownloadApp isMob />
            } */}
            {/* {ind === 1 && width < 991 && <AllWidget show={width < 991} index={0} />} */}
            {/* {ind === 1 && width < 767 && <WordleWidget />} */}
            <>{/* <WTC2023 /> */}</>
            {/* {((ind + 1) % 2 === 0) && isLoaded && (width < 991) && <AllWidget show={width < 991} index={((ind + 1) / 2) - 1} />} */}
          </React.Fragment>
        )
      })}
      {loading && articleLoader(['g', 's'])}
      {width < 767 && <AllWidget type={WIDGET.upcomingMatches} show />}
      {width < 767 && <AllWidget type={WIDGET.ranking} show />}
      {width < 767 && <Formula1Widget />}
    </>
  )
}
HomePageArticle.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.object)
}
export default HomePageArticle
